/* eslint-disable react/require-default-props */
import { useRef, useState } from "react"
import { createPortal } from "react-dom"
import { motion, AnimatePresence, useInView } from "framer-motion"
import cc from "classcat"
import { HandRaisedIcon } from "@heroicons/react/24/outline"

export type PlatformId =
  | "snapchat"
  | "tiktok"
  | "whatsapp"
  | "instagram"
  | "games"
  | "discord"
  | "other"

export type PlatformInfo = {
  id: PlatformId
  name: string
  /** Number of cases (or percentage) – used for bubble size */
  cases: number
  /** Optional width weight for layout; when omitted, `cases` is used. */
  sizeWeight?: number
  /** Tailwind background + text color classes for the bubble */
  tileClass: string
  /** SVG path(s) or simple icon representation */
  Icon: React.FC<{ className?: string }>
  /** Short stats line, e.g. "42% of reported cases" */
  stats: string
  /** Brief description of platform-specific risks */
  risksDescription: string
  /** Optional extra stat lines */
  extraStats?: string[]
}

const EMPTY_PLATFORM_STATS = ""
const EMPTY_PLATFORM_RISKS_DESCRIPTION = ""
const EMPTY_PLATFORM_EXTRA_STATS: string[] = []

export const defaultPlatforms: PlatformInfo[] = [
  {
    id: "snapchat",
    name: "Snapchat",
    cases: 25,
    sizeWeight: 24.5,
    tileClass: "bg-[#FFFC00] text-zinc-900",
    Icon: ({ className }) => (
      <svg
        viewBox="0 0 24 24"
        className={className}
        fill="currentColor"
        aria-hidden
      >
        <path d="M12.206.793c.99 0 4.347.276 5.93 3.821.529 1.193.403 3.219.299 4.847l-.003.06c-.012.18-.022.345-.03.51.075.045.203.09.401.09.3-.016.659-.12 1.033-.301.165-.088.344-.104.464-.104.182 0 .359.029.509.09.45.149.734.479.734.838.015.449-.39.839-1.213 1.168-.089.029-.209.075-.344.119-.45.135-1.139.36-1.333.81-.09.224-.061.524.12.868l.015.015c.06.136 1.526 3.475 4.791 4.014.255.044.435.27.42.509 0 .075-.015.149-.045.225-.24.569-1.273.988-3.146 1.271-.059.091-.12.375-.164.57-.029.179-.074.36-.134.553-.076.271-.27.405-.555.405h-.03c-.135 0-.313-.031-.538-.074-.36-.075-.765-.135-1.273-.135-.3 0-.599.015-.913.074-.6.104-1.123.464-1.723.884-.853.599-1.826 1.288-3.294 1.288-.06 0-.119-.015-.18-.015h-.149c-1.468 0-2.427-.69-3.279-1.288-.599-.42-1.107-.779-1.712-.884-.314-.045-.629-.074-.928-.074-.509 0-.913.059-1.272.135-.225.043-.404.074-.539.074-.285 0-.479-.134-.554-.405-.061-.192-.09-.374-.135-.553-.045-.195-.104-.479-.165-.57-1.918-.222-2.95-.642-3.189-1.226-.03-.075-.045-.149-.045-.225-.015-.239.165-.465.42-.509 3.264-.54 4.73-3.879 4.791-4.02l.016-.029c.18-.345.224-.645.119-.869-.195-.449-.884-.674-1.332-.809-.136-.045-.255-.09-.345-.119-.823-.329-1.212-.719-1.212-1.168 0-.359.284-.689.734-.838.149-.06.33-.09.509-.09.119 0 .299.015.464.104.374.181.733.285 1.033.301.198 0 .326-.045.401-.09-.008-.165-.018-.33-.03-.51l-.003-.06c-.104-1.628-.23-3.654.299-4.847 1.583-3.545 4.94-3.821 5.93-3.821z" />
      </svg>
    ),
    stats: EMPTY_PLATFORM_STATS,
    risksDescription: EMPTY_PLATFORM_RISKS_DESCRIPTION,
    extraStats: EMPTY_PLATFORM_EXTRA_STATS,
  },
  {
    id: "tiktok",
    name: "TikTok",
    cases: 20,
    sizeWeight: 19.5,
    tileClass: "bg-zinc-900 text-white",
    Icon: ({ className }) => (
      <svg
        viewBox="0 0 24 24"
        className={className}
        fill="currentColor"
        aria-hidden
      >
        <path d="M12.525 0.02C13.84 0 15.14.01 16.44 0c.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
      </svg>
    ),
    stats: EMPTY_PLATFORM_STATS,
    risksDescription: EMPTY_PLATFORM_RISKS_DESCRIPTION,
    extraStats: EMPTY_PLATFORM_EXTRA_STATS,
  },
  {
    id: "whatsapp",
    name: "WhatsApp",
    cases: 18,
    sizeWeight: 19,
    tileClass: "bg-[#25D366] text-white",
    Icon: ({ className }) => (
      <svg
        viewBox="0 0 24 24"
        className={className}
        fill="currentColor"
        aria-hidden
      >
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
      </svg>
    ),
    stats: EMPTY_PLATFORM_STATS,
    risksDescription: EMPTY_PLATFORM_RISKS_DESCRIPTION,
    extraStats: EMPTY_PLATFORM_EXTRA_STATS,
  },
  {
    id: "other",
    name: "Muut",
    cases: 12,
    sizeWeight: 10,
    tileClass: "bg-zinc-600 text-white",
    Icon: ({ className }) => (
      <svg
        viewBox="0 0 24 24"
        className={className}
        fill="currentColor"
        aria-hidden
      >
        <path d="M4 8h4V4H4v4zm6 12h4v-4h-4v4zm-6 0h4v-4H4v4zm0-6h4v-4H4v4zm6 0h4v-4h-4v4zm6-10v4h4V4h-4zm-6 4h4V4h-4v4zm6 6h4v-4h-4v4zm0 6h4v-4h-4v4z" />
      </svg>
    ),
    stats: EMPTY_PLATFORM_STATS,
    risksDescription: EMPTY_PLATFORM_RISKS_DESCRIPTION,
    extraStats: EMPTY_PLATFORM_EXTRA_STATS,
  },
  {
    id: "instagram",
    name: "Instagram",
    cases: 10,
    sizeWeight: 9.5,
    tileClass:
      "bg-gradient-to-br from-[#F58529] via-[#DD2A7B] to-[#8134AF] text-white",
    Icon: ({ className }) => (
      <svg
        viewBox="0 0 24 24"
        className={className}
        fill="currentColor"
        aria-hidden
      >
        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
      </svg>
    ),
    stats: EMPTY_PLATFORM_STATS,
    risksDescription: EMPTY_PLATFORM_RISKS_DESCRIPTION,
    extraStats: EMPTY_PLATFORM_EXTRA_STATS,
  },
  {
    id: "games",
    name: "Pelit",
    cases: 8,
    sizeWeight: 8.5,
    tileClass: "bg-violet-600 text-white",
    Icon: ({ className }) => (
      <svg
        viewBox="0 0 24 24"
        className={className}
        fill="currentColor"
        aria-hidden
      >
        <path d="M21.58 16.09l-1.09-7.66A3.996 3.996 0 0 0 16.53 5H7.47C5.48 5 3.79 6.46 3.51 8.42l-1.09 7.66C2.2 17.63 3.39 19 4.94 19c.68 0 1.32-.27 1.8-.75L9 16h6l2.25 2.25c.48.48 1.12.75 1.8.75 1.55 0 2.74-1.37 2.49-2.91zM11 11H9v2H8v-2H6v-1h2V8h1v2h2v1zm4-1.5c-.55 0-1 .45-1 1s.45 1 1 1 1-.45 1-1-.45-1-1-1zm2 2c-.55 0-1 .45-1 1s.45 1 1 1 1-.45 1-1-.45-1-1-1z" />
      </svg>
    ),
    stats: EMPTY_PLATFORM_STATS,
    risksDescription: EMPTY_PLATFORM_RISKS_DESCRIPTION,
    extraStats: EMPTY_PLATFORM_EXTRA_STATS,
  },
  {
    id: "discord",
    name: "Discord",
    cases: 7,
    sizeWeight: 8,
    tileClass: "bg-[#5865F2] text-white",
    Icon: ({ className }) => (
      <svg
        viewBox="0 0 24 24"
        className={className}
        fill="currentColor"
        aria-hidden
      >
        <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
      </svg>
    ),
    stats: EMPTY_PLATFORM_STATS,
    risksDescription: EMPTY_PLATFORM_RISKS_DESCRIPTION,
    extraStats: EMPTY_PLATFORM_EXTRA_STATS,
  },
]

type SocialPlatformDrawerProps = {
  platforms?: PlatformInfo[]
  platformTextOverrides?: Partial<
    Record<
      PlatformId,
      Pick<PlatformInfo, "name" | "stats" | "risksDescription" | "extraStats">
    >
  >
  className?: string
  /** Optional title shown above the platform bubbles. */
  sectionTitle?: string
  /** Hint shown above the bubbles to encourage hovering. Set to empty string to hide. */
  clickHint?: string
  /** When set, clicking a bubble selects it and parent shows content (no hover tooltip). */
  selectedId?: PlatformId | null
  /** Called when user clicks a bubble. Pass platform or null to close. Enables click-to-select mode. */
  onSelectPlatform?: (platform: PlatformInfo | null) => void
}

const TOOLTIP_SPACE_NEEDED = 320
const TOOLTIP_WIDTH = 384 // max-w-sm
const TOOLTIP_VIEWPORT_PADDING = 80 // 1.5rem – enough clearance on small screens so text isn’t cut

export function getRiskDescriptionParagraphs(platform: PlatformInfo): string[] {
  if (platform.risksDescription.includes("\n\n")) {
    return platform.risksDescription
      .split(/\n\s*\n/)
      .map((paragraph) => paragraph.trim())
      .filter(Boolean)
  }

  if (platform.id === "other") {
    return platform.risksDescription
      .split(/(?=Tähän prosenttiosuuteen)/)
      .map((paragraph) => paragraph.trim())
      .filter(Boolean)
  }

  return [platform.risksDescription.trim()].filter(Boolean)
}

function TooltipCard({
  platform,
  placement,
  arrowLeft,
  tooltipWidth,
}: {
  platform: PlatformInfo
  placement: "top" | "bottom"
  /** Arrow position from left of tooltip (px). When set, arrow points at the hovered bubble. */
  arrowLeft: number | null
  /** Width of the tooltip (for clamping arrow inside card). */
  tooltipWidth: number | null
}) {
  const isAbove = placement === "top"
  const descriptionParagraphs = getRiskDescriptionParagraphs(platform)
  const minArrow = 12
  const maxArrow = tooltipWidth != null ? tooltipWidth - 12 : 372
  const arrowStyle =
    arrowLeft != null
      ? {
          left: Math.max(minArrow, Math.min(arrowLeft, maxArrow)),
          transform: "translateX(-50%) rotate(45deg)",
        }
      : undefined
  return (
    <motion.div
      initial={{ opacity: 0, y: isAbove ? 6 : -6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: isAbove ? 6 : -6 }}
      transition={{ duration: 0.15 }}
      className="relative min-h-[20rem] rounded-xl border border-zinc-600/80 bg-zinc-800/95 p-4 shadow-xl ring-1 ring-zinc-700/50 backdrop-blur sm:min-h-[22rem] sm:p-5"
    >
      <div
        className={cc([
          "absolute h-3 w-3 rotate-45 border-zinc-600/80 bg-zinc-800/95",
          isAbove ? "-bottom-2 border-b border-r" : "-top-2 border-l border-t",
          arrowLeft == null && "left-1/2 -translate-x-1/2",
        ])}
        style={arrowStyle}
        aria-hidden
      />
      <div className="relative">
        <p className="text-sm font-semibold text-kkm-green">{platform.stats}</p>
        {descriptionParagraphs.map((paragraph, index) => (
          <p
            key={`${platform.id}-paragraph-${paragraph}`}
            className={cc([
              "text-sm leading-relaxed text-zinc-300",
              index === 0 ? "mt-3" : "mt-4",
            ])}
          >
            {paragraph}
          </p>
        ))}
        {platform.extraStats && platform.extraStats.length > 0 && (
          <ul className="mt-3 space-y-1.5">
            {platform.extraStats.map((line) => (
              <li
                key={`${platform.id}-extra-${line}`}
                className="flex items-start gap-2 text-xs text-zinc-400"
              >
                <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-zinc-500" />
                {line}
              </li>
            ))}
          </ul>
        )}
      </div>
    </motion.div>
  )
}

const defaultClickHint =
  "Hover over a bubble to see platform statistics and risks"

const rowVariants = {
  visible: {
    transition: { staggerChildren: 0.14, delayChildren: 0.2 },
  },
  hidden: {},
}

const row2Variants = {
  visible: {
    transition: { staggerChildren: 0.14, delayChildren: 0.5 },
  },
  hidden: {},
}

const boxVariants = {
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: "easeOut" },
  },
  hidden: { opacity: 0, y: 20 },
}

export default function SocialPlatformDrawer({
  platforms = defaultPlatforms,
  platformTextOverrides,
  className,
  sectionTitle,
  clickHint = defaultClickHint,
  selectedId = null,
  onSelectPlatform,
}: SocialPlatformDrawerProps) {
  const effectivePlatforms = platforms.map((platform) => {
    const override = platformTextOverrides?.[platform.id]
    if (!override) return platform
    return {
      ...platform,
      ...override,
      extraStats: override.extraStats ?? platform.extraStats,
    }
  })
  const [hoveredId, setHoveredId] = useState<PlatformId | null>(null)
  const [tooltipPosition, setTooltipPosition] = useState<{
    left: number
    top?: number
    bottom?: number
    placement: "top" | "bottom"
    width: number
    /** Bubble center X (viewport) so the arrow can point at the hovered bubble */
    anchorCenterX: number
  } | null>(null)
  const sectionRef = useRef<HTMLDivElement>(null)
  const isInView = useInView(sectionRef, { amount: 0.15, once: true })

  const handleBoxMouseEnter = (
    e: React.MouseEvent<HTMLElement>,
    id: PlatformId,
  ) => {
    const rect = e.currentTarget.getBoundingClientRect()
    if (typeof window !== "undefined") {
      const winW = window.innerWidth
      const winH = window.innerHeight
      const spaceBelow = winH - rect.bottom
      const spaceAbove = rect.top
      let placement: "top" | "bottom" = "bottom"
      if (spaceBelow >= TOOLTIP_SPACE_NEEDED) {
        placement = "bottom"
      } else if (spaceAbove >= TOOLTIP_SPACE_NEEDED) {
        placement = "top"
      }
      const centerX = rect.left + rect.width / 2
      // Tooltip uses w-[calc(100vw-2rem)] max-w-sm so its width is min(384, winW - 32) – use that for clamping
      const actualWidth = Math.min(TOOLTIP_WIDTH, winW - 32)
      const actualHalfWidth = actualWidth / 2
      const left = Math.max(
        TOOLTIP_VIEWPORT_PADDING,
        Math.min(
          centerX - actualHalfWidth,
          winW - TOOLTIP_VIEWPORT_PADDING - actualWidth,
        ),
      )
      const top = placement === "bottom" ? rect.bottom + 8 : undefined
      const bottom = placement === "top" ? winH - rect.top + 8 : undefined
      setTooltipPosition({
        left,
        top,
        bottom,
        placement,
        width: actualWidth,
        anchorCenterX: centerX,
      })
    } else {
      setTooltipPosition({
        left: 0,
        top: 0,
        placement: "bottom",
        width: TOOLTIP_WIDTH,
        anchorCenterX: 0,
      })
    }
    setHoveredId(id)
  }

  const row1 = effectivePlatforms.slice(0, 3)
  const row2Source = effectivePlatforms.slice(3, 7)
  const row2Order: PlatformId[] = ["instagram", "games", "discord", "other"]
  const row2 = row2Order
    .map((id) => row2Source.find((platform) => platform.id === id))
    .filter((platform): platform is PlatformInfo => Boolean(platform))
  const gridColsRow1 = row1.map((p) => `${p.sizeWeight ?? p.cases}fr`).join(" ")
  const gridColsRow2 = row2.map((p) => `${p.sizeWeight ?? p.cases}fr`).join(" ")

  const isClickMode = typeof onSelectPlatform === "function"
  const renderBox = (platform: PlatformInfo) => {
    const isHovered = hoveredId === platform.id
    const isSelected = isClickMode && selectedId === platform.id
    const isHighlighted = isHovered || isSelected
    const isGrayedOut =
      isClickMode && hoveredId != null && hoveredId !== platform.id
    return (
      <motion.div
        key={platform.id}
        className="min-w-0 transition-[opacity,filter] duration-200"
        style={{
          opacity: isGrayedOut ? 0.4 : 1,
          filter: isGrayedOut ? "grayscale(0.6) brightness(0.85)" : "none",
        }}
      >
        <button
          type="button"
          className={cc([
            "relative flex min-h-[120px] w-full cursor-pointer flex-col rounded-xl border-2 p-1 transition-all sm:min-h-[140px] sm:p-1.5",
            isHighlighted
              ? "border-kkm-green/70 ring-2 ring-kkm-green/50 ring-offset-2 ring-offset-zinc-700"
              : "border-zinc-600/60 hover:border-zinc-500",
          ])}
          onMouseEnter={(e) => {
            if (isClickMode) {
              setHoveredId(platform.id)
            } else {
              handleBoxMouseEnter(e, platform.id)
            }
          }}
          onMouseLeave={() => {
            setHoveredId(null)
            if (!isClickMode) setTooltipPosition(null)
          }}
          onClick={() => {
            if (!isClickMode) return
            onSelectPlatform?.(selectedId === platform.id ? null : platform)
          }}
          onKeyDown={
            isClickMode
              ? (e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault()
                    onSelectPlatform?.(
                      selectedId === platform.id ? null : platform,
                    )
                  }
                }
              : undefined
          }
          aria-label={`${platform.name}: ${platform.cases} % of cases.${isClickMode ? " Click for details." : " Hover for details."}`}
        >
          <div
            className={cc([
              "flex min-h-0 min-w-0 w-full flex-1 flex-col items-center justify-center rounded-lg p-3 transition-shadow sm:p-4",
              platform.tileClass,
            ])}
          >
            <platform.Icon className="h-8 w-8 shrink-0 sm:h-10 sm:w-10" />
            <span className="mt-1.5 w-full truncate text-center text-sm font-semibold sm:text-base">
              {platform.name}
            </span>
            <span className="mt-0.5 text-sm font-semibold opacity-90">
              {platform.cases} %
            </span>
          </div>
        </button>
      </motion.div>
    )
  }

  return (
    <div ref={sectionRef} className={cc(["space-y-4", className])}>
      {sectionTitle ? (
        <p className="text-center text-base font-semibold text-zinc-400 sm:text-left">
          {sectionTitle}
        </p>
      ) : null}
      {clickHint ? (
        <p className="flex items-center justify-center gap-2 text-sm text-zinc-400 sm:justify-start">
          <HandRaisedIcon
            className="h-4 w-4 shrink-0 text-kkm-green"
            aria-hidden
          />
          <span>{clickHint}</span>
        </p>
      ) : null}

      <div className="flex w-full flex-col gap-2 sm:gap-3" role="list">
        {row1.length > 0 && (
          <motion.div
            className="grid w-full gap-1.5 sm:gap-2"
            style={{ gridTemplateColumns: gridColsRow1 }}
            variants={rowVariants}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
          >
            {row1.map((platform) => (
              <motion.div
                key={platform.id}
                className="min-w-0"
                variants={boxVariants}
              >
                {renderBox(platform)}
              </motion.div>
            ))}
          </motion.div>
        )}
        {row2.length > 0 && (
          <motion.div
            className="grid w-full gap-1.5 sm:gap-2"
            style={{ gridTemplateColumns: gridColsRow2 }}
            variants={row2Variants}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
          >
            {row2.map((platform) => (
              <motion.div
                key={platform.id}
                className="min-w-0"
                variants={boxVariants}
              >
                {renderBox(platform)}
              </motion.div>
            ))}
          </motion.div>
        )}
      </div>

      {/* Tooltip in portal (only when not in click mode) */}
      {!isClickMode &&
        hoveredId &&
        tooltipPosition &&
        typeof document !== "undefined" &&
        createPortal(
          (() => {
            const platform = effectivePlatforms.find((p) => p.id === hoveredId)
            if (!platform) return null
            return (
              <AnimatePresence>
                <motion.div
                  key={hoveredId}
                  role="tooltip"
                  className="fixed z-[9999] min-w-0 max-w-[min(384px,100vw-4rem)]"
                  style={{
                    left: tooltipPosition.left,
                    top: tooltipPosition.top,
                    bottom: tooltipPosition.bottom,
                    width: tooltipPosition.width,
                  }}
                  initial={{
                    opacity: 0,
                    y: tooltipPosition.placement === "bottom" ? -6 : 6,
                  }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{
                    opacity: 0,
                    y: tooltipPosition.placement === "bottom" ? -6 : 6,
                  }}
                  transition={{ duration: 0.15 }}
                >
                  <TooltipCard
                    platform={platform}
                    placement={tooltipPosition.placement}
                    arrowLeft={
                      tooltipPosition.anchorCenterX - tooltipPosition.left
                    }
                    tooltipWidth={tooltipPosition.width}
                  />
                </motion.div>
              </AnimatePresence>
            )
          })(),
          document.body,
        )}
    </div>
  )
}
